import React, { useState } from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import {
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AnimatedErrorMessage from "../animatedErrorMessage";

export interface FormInputProps {
  name: string;
  type?: "text" | "password" | "email" | "number" | "tel" | "checkbox";
  rules?: RegisterOptions;
  icon?: React.ReactNode | string;
  placeholder?: string;
  checkboxLabel?: React.ReactNode;
  validateOnChange?: boolean;
  disabled?: boolean;
}

const renderIcon = (icon: React.ReactNode | string) => {
  if (typeof icon === "string") {
    return <i className={icon} style={{ color: "#64748B", fontSize: "18px" }} />;
  }
  return icon;
};

const FormInput: React.FC<FormInputProps> = ({
  name,
  type = "text",
  rules = {},
  icon,
  placeholder = "",
  checkboxLabel,
  validateOnChange = true,
  disabled = false,
}) => {
  const {
    control,
    trigger,
    formState: { errors, isSubmitted },
  } = useFormContext();

  const [showPassword, setShowPassword] = useState(false);

  const shouldShowError = validateOnChange || isSubmitted;
  const fieldError = shouldShowError ? errors[name] : undefined;

  if (type === "checkbox") {
    return (
      <FormControl error={!!fieldError}>
        <Controller
          name={name}
          control={control}
          defaultValue={false}
          rules={rules}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={disabled}
                  sx={{
                    color: fieldError ? "error.main" : undefined,
                    "&.Mui-checked": {
                      color: "primary.main",
                    },
                  }}
                />
              }
              label={checkboxLabel}
            />
          )}
        />
        <AnimatedErrorMessage
          show={!!fieldError}
          message={fieldError?.message?.toString()}
          variant="form"
        />
      </FormControl>
    );
  }

  return (
    <div>
      <Controller
        name={name}
        control={control}
        defaultValue=""
        rules={rules}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            type={type === "password" ? (showPassword ? "text" : "password") : type}
            placeholder={placeholder}
            disabled={disabled}
            error={!!fieldError}
            onChange={e => {
              field.onChange(e);
              if (errors[name]) {
                trigger(name as any);
              }
            }}
            slotProps={{
              input: {
                startAdornment: icon ? (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>{renderIcon(icon)}</InputAdornment>
                ) : undefined,
                endAdornment: type === "password" ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(prev => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fff",
                fontSize: "15px",
                fontFamily: '"Inter", sans-serif',
                "& fieldset": {
                  borderColor: "#E2E8F0",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: "#CBD5E1",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#0078D4",
                  borderWidth: "1px",
                },
                "&.Mui-error fieldset": {
                  borderColor: "#ED4F9D",
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "16px 14px",
                fontSize: "15px",
                fontFamily: '"Inter", sans-serif',
                color: "#334155",
                fontWeight: 500,
                letterSpacing: "-0.01em",
                "&::placeholder": {
                  color: "#8A99AE",
                  opacity: 1,
                },
              },
              "& .MuiInputAdornment-root": {
                color: "#64748B",
              },
            }}
          />
        )}
      />
      <AnimatedErrorMessage
        show={!!fieldError}
        message={fieldError?.message?.toString()}
        variant="form"
      />
    </div>
  );
};

export default FormInput;
